import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Receipt, Save, Loader2, FileText, ClipboardList, Info, Hash, Percent, Landmark, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

export default function BillingAdmin() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState({
    shop_tax_rate: 20.0,
    shop_tax_included: true,
    vat_applicable: true,
    vat_exempt_mention: "TVA non applicable, art. 293 B du CGI",
    invoice_prefix: "FA",
    invoice_next_number: 1,
    invoice_delivery_prefix: "BL",
    invoice_delivery_next_number: 1,
    invoice_purchase_prefix: "BC",
    invoice_purchase_next_number: 1,
    invoice_auto_generate: true,
    invoice_payment_terms: "",
    invoice_bank_details: "",
    invoice_legal_mention: "",
    invoice_vat_rates: [],
  });
  const [invoices, setInvoices] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setForm({
      shop_tax_rate: settings.shop_tax_rate ?? 20.0,
      shop_tax_included: settings.shop_tax_included !== false,
      vat_applicable: settings.vat_applicable !== false,
      vat_exempt_mention: settings.vat_exempt_mention || "TVA non applicable, art. 293 B du CGI",
      invoice_prefix: settings.invoice_prefix || "FA",
      invoice_next_number: settings.invoice_next_number ?? 1,
      invoice_delivery_prefix: settings.invoice_delivery_prefix || "BL",
      invoice_delivery_next_number: settings.invoice_delivery_next_number ?? 1,
      invoice_purchase_prefix: settings.invoice_purchase_prefix || "BC",
      invoice_purchase_next_number: settings.invoice_purchase_next_number ?? 1,
      invoice_auto_generate: settings.invoice_auto_generate !== false,
      invoice_payment_terms: settings.invoice_payment_terms || "",
      invoice_bank_details: settings.invoice_bank_details || "",
      invoice_legal_mention: settings.invoice_legal_mention || "",
      invoice_vat_rates: settings.invoice_vat_rates || [],
    });
    setDirty(false);
  }, [settings]);

  useEffect(() => {
    api.get("/admin/invoices").then(r => setInvoices(r.data || [])).catch(() => setInvoices([]));
  }, []);

  const patch = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      await refresh();
      setDirty(false);
      toast.success("Paramètres de facturation enregistrés");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const downloadPdf = async (inv) => {
    try {
      const token = localStorage.getItem("kap_token") || "";
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${inv.order_id}/invoice/download?doc_type=${inv.doc_type}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { toast.error("Erreur téléchargement"); return; }
      const blob = await r.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="billing-admin">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-1">
            <Receipt className="w-3.5 h-3.5" /> Facturation &amp; TVA
          </div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Facturation &amp; TVA</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
            Configurez vos <strong>taux de TVA</strong>, la numérotation légale des <strong>factures / BL / BC</strong> et les mentions obligatoires.
            Les factures sont générées automatiquement à chaque commande payée.
          </p>
        </div>
        <Button onClick={save} disabled={!dirty || saving} className="bg-[color:var(--brand-navy)]" data-testid="billing-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          {dirty ? "Enregistrer" : "Enregistré"}
        </Button>
      </header>

      {/* TVA */}
      <Card className="p-5 space-y-4" data-testid="billing-vat-section">
        <div className="flex items-center gap-2">
          <Percent className="w-4 h-4 text-[color:var(--brand-sky)]" />
          <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Régime de TVA</h3>
        </div>

        {/* Toggle assujetti / franchise en base */}
        <div className="grid md:grid-cols-2 gap-3">
          <label
            onClick={() => patch("vat_applicable", true)}
            className={`p-4 rounded-xl border-2 cursor-pointer transition ${form.vat_applicable ? "border-[color:var(--brand-sky)] bg-sky-50/50" : "border-slate-200 hover:border-slate-300"}`}
            data-testid="vat-applicable-yes"
          >
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full border-2 shrink-0 mt-0.5 ${form.vat_applicable ? "border-[color:var(--brand-sky)] bg-[color:var(--brand-sky)]" : "border-slate-300"}`}>
                {form.vat_applicable && <div className="w-full h-full rounded-full bg-white scale-[0.4]" />}
              </div>
              <div>
                <div className="text-sm font-bold text-[color:var(--brand-navy)]">Entreprise assujettie à la TVA</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">SAS, SARL, auto-entrepreneur au-dessus du seuil, etc. Les factures affichent HT / TVA / TTC.</div>
              </div>
            </div>
          </label>
          <label
            onClick={() => patch("vat_applicable", false)}
            className={`p-4 rounded-xl border-2 cursor-pointer transition ${!form.vat_applicable ? "border-amber-500 bg-amber-50/50" : "border-slate-200 hover:border-slate-300"}`}
            data-testid="vat-applicable-no"
          >
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full border-2 shrink-0 mt-0.5 ${!form.vat_applicable ? "border-amber-500 bg-amber-500" : "border-slate-300"}`}>
                {!form.vat_applicable && <div className="w-full h-full rounded-full bg-white scale-[0.4]" />}
              </div>
              <div>
                <div className="text-sm font-bold text-[color:var(--brand-navy)]">Franchise en base de TVA</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">Micro-entreprise sous seuil CGI art. 293 B. Factures sans TVA, mention légale obligatoire.</div>
              </div>
            </div>
          </label>
        </div>

        {!form.vat_applicable && (
          <div className="p-3 rounded-xl border border-amber-300 bg-amber-50" data-testid="vat-exempt-mention-wrapper">
            <Label className="text-xs font-bold text-amber-900">Mention obligatoire affichée en bas de facture</Label>
            <Input
              value={form.vat_exempt_mention}
              onChange={e => patch("vat_exempt_mention", e.target.value)}
              className="mt-1.5 text-sm bg-white"
              data-testid="vat-exempt-mention"
            />
            <div className="text-[11px] text-amber-700 mt-1.5">Exemple par défaut : « TVA non applicable, art. 293 B du CGI »</div>
          </div>
        )}

        {form.vat_applicable && (
        <>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-bold">Taux de TVA par défaut</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <Input
                type="number" step="0.1" min="0" max="30"
                value={form.shop_tax_rate}
                onChange={e => patch("shop_tax_rate", parseFloat(e.target.value) || 0)}
                className="w-32"
                data-testid="vat-default-rate"
              />
              <span className="text-sm">%</span>
              <span className="text-[11px] text-[hsl(var(--muted-foreground))] ml-2">Appliqué si le produit n'a pas de taux personnalisé</span>
            </div>
          </div>
          <label className="flex items-start gap-3 p-3 rounded-xl border bg-[hsl(var(--muted))]/30 cursor-pointer" data-testid="vat-included-wrapper">
            <Switch checked={form.shop_tax_included} onCheckedChange={v => patch("shop_tax_included", v)} />
            <div>
              <div className="text-sm font-bold text-[color:var(--brand-navy)]">Prix affichés TTC</div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))]">B2C = TTC (activé) · B2B = HT (désactivé)</div>
            </div>
          </label>
        </div>

        <div>
          <Label className="text-xs font-bold">Taux disponibles (saisissables sur chaque produit)</Label>
          <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="vat-rates-list">
            {(form.invoice_vat_rates || []).map((r, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 rounded-lg border bg-white">
                <Input
                  type="number" step="0.1" min="0" max="30"
                  value={r.rate}
                  onChange={e => {
                    const copy = [...form.invoice_vat_rates];
                    copy[idx] = { ...copy[idx], rate: parseFloat(e.target.value) || 0 };
                    patch("invoice_vat_rates", copy);
                  }}
                  className="w-20 text-sm"
                />
                <Input
                  value={r.label}
                  onChange={e => {
                    const copy = [...form.invoice_vat_rates];
                    copy[idx] = { ...copy[idx], label: e.target.value };
                    patch("invoice_vat_rates", copy);
                  }}
                  className="flex-1 text-sm"
                  placeholder="Taux normal 20 %"
                />
                <Button variant="outline" size="sm" onClick={() => patch("invoice_vat_rates", form.invoice_vat_rates.filter((_, i) => i !== idx))} className="text-red-600 shrink-0">✕</Button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => patch("invoice_vat_rates", [...(form.invoice_vat_rates || []), { rate: 0, label: "Nouveau taux" }])} className="mt-2 text-xs">
            + Ajouter un taux
          </Button>
        </div>
        </>
        )}
      </Card>

      {/* Numérotation */}
      <Card className="p-5 space-y-4" data-testid="billing-numbering-section">
        <div className="flex items-center gap-2">
          <Hash className="w-4 h-4 text-[color:var(--brand-sky)]" />
          <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Numérotation des documents</h3>
        </div>
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>Obligatoire (Article 289-II CGI)</strong> : la numérotation des factures doit être <strong>chronologique et sans rupture</strong>.
            Ne modifiez le prochain numéro que pour une mise en conformité (ex : reprise d'une séquence existante).
            Format : <code className="font-mono">[PREFIX]-[ANNÉE]-[5 chiffres]</code>.
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { prefix: "invoice_prefix", next: "invoice_next_number", label: "Factures", icon: Receipt, example: `${form.invoice_prefix}-${new Date().getFullYear()}-${String(form.invoice_next_number).padStart(5, "0")}` },
            { prefix: "invoice_delivery_prefix", next: "invoice_delivery_next_number", label: "Bons de livraison", icon: ClipboardList, example: `${form.invoice_delivery_prefix}-${new Date().getFullYear()}-${String(form.invoice_delivery_next_number).padStart(5, "0")}` },
            { prefix: "invoice_purchase_prefix", next: "invoice_purchase_next_number", label: "Bons de commande", icon: FileText, example: `${form.invoice_purchase_prefix}-${new Date().getFullYear()}-${String(form.invoice_purchase_next_number).padStart(5, "0")}` },
          ].map((row, i) => (
            <div key={i} className="rounded-xl border p-4 space-y-3 bg-[hsl(var(--muted))]/20" data-testid={`numbering-${row.prefix}`}>
              <div className="flex items-center gap-2">
                <row.icon className="w-4 h-4 text-[color:var(--brand-navy)]" />
                <div className="font-bold text-sm text-[color:var(--brand-navy)]">{row.label}</div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">Préfixe</Label>
                <Input value={form[row.prefix]} onChange={e => patch(row.prefix, e.target.value.slice(0, 4).toUpperCase())} className="mt-1 text-sm font-mono" maxLength={4} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">Prochain numéro</Label>
                <Input type="number" min="1" value={form[row.next]} onChange={e => patch(row.next, parseInt(e.target.value) || 1)} className="mt-1 text-sm" />
              </div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))]">Prévisualisation : <code className="font-mono text-[color:var(--brand-navy)] font-semibold">{row.example}</code></div>
            </div>
          ))}
        </div>
      </Card>

      {/* Mentions légales + bank */}
      <Card className="p-5 space-y-4" data-testid="billing-legal-section">
        <div className="flex items-center gap-2">
          <Landmark className="w-4 h-4 text-[color:var(--brand-sky)]" />
          <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Mentions &amp; coordonnées bancaires</h3>
        </div>
        <div>
          <Label className="text-xs font-bold">Conditions de paiement (affiché en pied de facture)</Label>
          <Textarea rows={2} value={form.invoice_payment_terms} onChange={e => patch("invoice_payment_terms", e.target.value)} className="mt-1.5 text-sm" data-testid="invoice-payment-terms" />
        </div>
        <div>
          <Label className="text-xs font-bold">Coordonnées bancaires (IBAN / RIB)</Label>
          <Textarea rows={2} value={form.invoice_bank_details} onChange={e => patch("invoice_bank_details", e.target.value)} placeholder="IBAN : FR76 ... · BIC : ..." className="mt-1.5 text-sm font-mono" data-testid="invoice-bank-details" />
        </div>
        <div>
          <Label className="text-xs font-bold">Mentions légales additionnelles</Label>
          <Textarea rows={2} value={form.invoice_legal_mention} onChange={e => patch("invoice_legal_mention", e.target.value)} placeholder="Auto-entrepreneur : TVA non applicable, art. 293 B du CGI…" className="mt-1.5 text-sm" data-testid="invoice-legal-mention" />
        </div>
        <label className="flex items-start gap-3 p-3 rounded-xl border bg-[hsl(var(--muted))]/30 cursor-pointer" data-testid="auto-generate-wrapper">
          <Switch checked={form.invoice_auto_generate} onCheckedChange={v => patch("invoice_auto_generate", v)} />
          <div>
            <div className="text-sm font-bold text-[color:var(--brand-navy)]">Générer automatiquement la facture</div>
            <div className="text-[11px] text-[hsl(var(--muted-foreground))]">À chaque commande payée, un numéro de facture est attribué automatiquement.</div>
          </div>
        </label>
      </Card>

      {/* Journal facturier */}
      <Card className="p-5" data-testid="billing-journal">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-[color:var(--brand-sky)]" />
            <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Journal des documents</h3>
          </div>
          <Button variant="outline" size="sm" onClick={() => api.get("/admin/invoices").then(r => setInvoices(r.data || []))} className="text-xs">
            <RefreshCw className="w-3 h-3 mr-1" /> Actualiser
          </Button>
        </div>
        {invoices.length === 0 ? (
          <div className="text-center py-10 text-sm text-[hsl(var(--muted-foreground))]">
            Aucune facture encore générée. Les factures s'affichent ici dès qu'une commande est payée.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--muted))]/50 text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                <tr>
                  <th className="text-left p-3 font-bold">N°</th>
                  <th className="text-left p-3 font-bold">Type</th>
                  <th className="text-left p-3 font-bold">Client</th>
                  <th className="text-right p-3 font-bold">Total TTC</th>
                  <th className="text-left p-3 font-bold">Date</th>
                  <th className="text-right p-3 font-bold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {invoices.map(inv => (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.number}`} className="hover:bg-[hsl(var(--muted))]/20">
                    <td className="p-3 font-mono font-semibold text-[color:var(--brand-navy)]">{inv.number}</td>
                    <td className="p-3">
                      <Badge className="bg-sky-100 text-sky-700 border-0 text-[10px] font-bold">
                        {inv.doc_type === "invoice" ? "Facture" : inv.doc_type === "delivery" ? "BL" : "BC"}
                      </Badge>
                    </td>
                    <td className="p-3 text-xs">{inv.customer_name || inv.customer_email}</td>
                    <td className="p-3 text-right font-bold tabular-nums">{(inv.total_ttc_cents / 100).toFixed(2)} €</td>
                    <td className="p-3 text-xs text-[hsl(var(--muted-foreground))]">{new Date(inv.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(inv)} className="text-xs" data-testid={`download-${inv.number}`}>
                        <FileText className="w-3 h-3 mr-1" /> PDF
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {dirty && (
        <div className="sticky bottom-4 flex justify-center z-30">
          <Card className="p-3 bg-[color:var(--brand-navy)] text-white border-0 shadow-2xl flex items-center gap-3">
            <span className="text-xs">Modifications non enregistrées</span>
            <Button size="sm" onClick={save} disabled={saving} className="bg-[color:var(--brand-sky)]">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Enregistrer
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
}
